import React, { useState, useEffect, useCallback } from 'react';
import { FileBarChart, Download, TrendingUp, TrendingDown, Minus, Users, FileText, Package, Calendar, ChevronLeft, ChevronRight } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { API } from '@/utils/api';
import axios from 'axios';
import AdminLayout from '@/components/admin/AdminLayout';
import { useTranslation } from 'react-i18next';

const MONTH_NAMES = ['Janvier', 'Fevrier', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Aout', 'Septembre', 'Octobre', 'Novembre', 'Decembre'];

const ReportsPage = ({ auth, onLogout }) => {
  const { t } = useTranslation();
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/reports/monthly/data/${year}/${month}`);
      setData(res.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [year, month]);

  useEffect(() => { loadData(); }, [loadData]);

  const prevMonth = () => {
    if (month === 1) { setMonth(12); setYear(y => y - 1); }
    else setMonth(m => m - 1);
  };
  const nextMonth = () => {
    if (month === 12) { setMonth(1); setYear(y => y + 1); }
    else setMonth(m => m + 1);
  };

  const downloadPdf = async () => {
    try {
      const { downloadPdf: safePdf } = await import('@/utils/api');
      await safePdf(`${API}/reports/monthly/${year}/${month}`, `rapport_${year}_${month}.pdf`);
    } catch (err) {
      console.error('PDF download error:', err);
    }
  };

  const ChangeIndicator = ({ value }) => {
    if (!value || value === 0) return <span className="text-slate-400 flex items-center gap-1 text-xs"><Minus className="w-3 h-3" /> 0%</span>;
    if (value > 0) return <span className="text-green-600 flex items-center gap-1 text-xs"><TrendingUp className="w-3 h-3" /> +{value}%</span>;
    return <span className="text-red-600 flex items-center gap-1 text-xs"><TrendingDown className="w-3 h-3" /> {value}%</span>;
  };

  return (
    <AdminLayout auth={auth} onLogout={onLogout}>
      <div className="p-4 md:p-8 space-y-6 max-w-7xl mx-auto">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-4xl font-heading font-bold text-slate-900" data-testid="reports-title">Rapports mensuels</h1>
            <p className="text-base text-slate-600 mt-1">Tableau de bord de performance mensuelle</p>
          </div>
          <Button onClick={downloadPdf} data-testid="download-pdf-btn">
            <Download className="w-4 h-4 mr-2" /> Telecharger PDF
          </Button>
        </div>

        {/* Month navigator */}
        <div className="flex items-center gap-4 justify-center">
          <Button variant="outline" size="icon" onClick={prevMonth} data-testid="prev-month-btn">
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <span className="text-lg font-semibold text-slate-900 min-w-[200px] text-center" data-testid="current-month">
            {MONTH_NAMES[month - 1]} {year}
          </span>
          <Button variant="outline" size="icon" onClick={nextMonth} data-testid="next-month-btn">
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>

        {loading ? (
          <div className="text-slate-400 text-center py-20">{t('common.loading')}</div>
        ) : !data ? (
          <Card className="p-12 text-center">
            <FileBarChart className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500">Aucune donnee pour cette periode</p>
          </Card>
        ) : (
          <>
            {/* KPI Cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <Card className="p-5" data-testid="kpi-revenue">
                <div className="flex items-center justify-between">
                  <p className="text-xs text-slate-500 uppercase tracking-wider">Chiffre d'affaires</p>
                  <ChangeIndicator value={data.revenue_change} />
                </div>
                <p className="text-2xl font-bold text-slate-900 mt-1">CHF {data.revenue?.toFixed(2)}</p>
                <p className="text-xs text-slate-400 mt-1">vs CHF {data.prev_revenue?.toFixed(2)} mois precedent</p>
              </Card>
              <Card className="p-5" data-testid="kpi-invoices">
                <p className="text-xs text-slate-500 uppercase tracking-wider">Factures</p>
                <p className="text-2xl font-bold text-slate-900 mt-1">{data.invoices_count}</p>
                <p className="text-xs text-slate-400 mt-1">{data.invoices_paid} payees / {data.invoices_unpaid} impayees</p>
              </Card>
              <Card className="p-5" data-testid="kpi-work-orders">
                <p className="text-xs text-slate-500 uppercase tracking-wider">Fiches de travail</p>
                <p className="text-2xl font-bold text-slate-900 mt-1">{data.wo_total}</p>
                <p className="text-xs text-slate-400 mt-1">{data.wo_completed} terminees / {data.wo_in_progress} en cours</p>
              </Card>
              <Card className="p-5" data-testid="kpi-clients">
                <p className="text-xs text-slate-500 uppercase tracking-wider">Nouveaux clients</p>
                <p className="text-2xl font-bold text-slate-900 mt-1">{data.new_clients}</p>
                <p className="text-xs text-slate-400 mt-1">{data.appointments_total} RDV ce mois</p>
              </Card>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Stock summary */}
              <Card className="p-6">
                <h2 className="text-base font-semibold text-slate-900 mb-4 flex items-center gap-2">
                  <Package className="w-5 h-5 text-blue-600" /> Etat du stock
                </h2>
                <div className="grid grid-cols-3 gap-4 text-center mb-4">
                  <div>
                    <p className="text-2xl font-bold text-slate-900">{data.parts_count}</p>
                    <p className="text-xs text-slate-500">Pieces</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-900">CHF {data.stock_value?.toFixed(0)}</p>
                    <p className="text-xs text-slate-500">Valeur</p>
                  </div>
                  <div>
                    <p className={`text-2xl font-bold ${data.low_stock?.length > 0 ? 'text-red-600' : 'text-green-600'}`}>{data.low_stock?.length || 0}</p>
                    <p className="text-xs text-slate-500">Stock bas</p>
                  </div>
                </div>
                {data.low_stock?.length > 0 && (
                  <div className="space-y-1">
                    {data.low_stock.slice(0, 5).map((p, i) => (
                      <div key={i} className="flex justify-between text-sm bg-red-50 rounded px-3 py-1.5">
                        <span className="text-slate-700">{p.name}</span>
                        <span className="text-red-600 font-medium">{Math.floor(p.quantity)}/{Math.floor(p.min_stock)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </Card>

              {/* Top clients */}
              <Card className="p-6">
                <h2 className="text-base font-semibold text-slate-900 mb-4 flex items-center gap-2">
                  <Users className="w-5 h-5 text-indigo-600" /> Top clients
                </h2>
                {data.top_clients?.length > 0 ? (
                  <div className="space-y-2">
                    {data.top_clients.map((c, i) => (
                      <div key={i} className="flex justify-between items-center text-sm">
                        <div className="flex items-center gap-2">
                          <span className="w-6 h-6 rounded-full bg-indigo-100 text-indigo-700 text-xs flex items-center justify-center font-bold">{i + 1}</span>
                          <span className="text-slate-700">{c.name}</span>
                        </div>
                        <span className="font-semibold text-slate-900">CHF {c.revenue?.toFixed(2)}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-slate-400">Aucun client ce mois</p>
                )}
                {data.new_clients_names?.length > 0 && (
                  <div className="mt-4 pt-3 border-t">
                    <p className="text-xs text-slate-400 uppercase tracking-wider mb-2">Nouveaux clients</p>
                    <p className="text-sm text-slate-600">{data.new_clients_names.join(', ')}</p>
                  </div>
                )}
              </Card>
            </div>

            {/* Top parts */}
            {data.top_parts?.length > 0 && (
              <Card className="p-6">
                <h2 className="text-base font-semibold text-slate-900 mb-4 flex items-center gap-2">
                  <Package className="w-5 h-5 text-amber-600" /> Top pieces vendues
                </h2>
                <div className="space-y-2">
                  {data.top_parts.map((p, i) => (
                    <div key={i} className="flex items-center gap-3 text-sm">
                      <span className="w-6 h-6 rounded-full bg-amber-100 text-amber-700 text-xs flex items-center justify-center font-bold shrink-0">{i + 1}</span>
                      <span className="text-slate-700 flex-1">{p.name}</span>
                      <span className="font-semibold text-slate-900">{Math.floor(p.qty)} vendues</span>
                    </div>
                  ))}
                </div>
              </Card>
            )}
          </>
        )}
      </div>
    </AdminLayout>
  );
};

export default ReportsPage;
